/**
 * The house's week (docs/house-week.md): the house goal (flag `houseGoalUI`) and the week's theme
 * (flag `themeWeekUI`). Owner, 2026-10-29, asked "What are other ways to increase engagement?" and
 * picked, from a short list:
 *
 * - "House goal of the week. One shared target on the notice board ("30 chores this week") with a
 *   progress bar. If the house hits it, everyone gets wood toward an upgrade. It's the only team-up
 *   idea; everything else is competitive."
 * - "Themed weeks. A rotating weekly theme ("Kitchen week", "Omie week" for dog chores) where
 *   matching chores pay extra and the board gets a banner. It keeps the same chores feeling fresh."
 *
 * Both run on the household's week: Monday 00:00 to the next Monday, in the household's zone, keyed as
 * the weekly scroll's ISO week ("2026-W44", shared/weekly.ts weekKey). Pure, so both clients, the
 * server and the Node tests import it.
 */
import { DISH_WORDS, LAUNDRY_WORDS, isOmieJob, titleMentions, titleMentionsWord } from "./achievements.ts";
import { addDays, localParts, weekKey, zonedInstant } from "./weekly.ts";

// ---------- the week ----------

/** The household week holding `now`: its ISO key and its first and last instants (Monday 00:00 to the next), in `tz`. */
export function houseWeek(now: Date, tz: string): { key: string; start: string; end: string } {
  const { y, m, d } = localParts(now, tz);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  const monday = addDays(y, m, d, -((wd + 6) % 7));
  const next = addDays(monday.y, monday.m, monday.d, 7);
  return {
    key: weekKey(now, tz),
    start: zonedInstant(monday.y, monday.m, monday.d, 0, tz).toISOString(),
    end: zonedInstant(next.y, next.m, next.d, 0, tz).toISOString(),
  };
}

/** This device's zone (the clients' stand-in for the household's: the theme turns at its Monday midnight). */
export function deviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Toronto";
  } catch {
    return "America/Toronto";
  }
}

/** A week key's running number ("2026-W44" to 2026 * 53 + 44): consecutive weeks differ by one, except across a year. */
export function weekNumber(key: string): number {
  const m = /^(\d{4})-W(\d{2})$/.exec(key);
  return m ? Number(m[1]) * 53 + Number(m[2]) : 0;
}

// ---------- the themes ----------

export type ThemeKey = "kitchen" | "omie" | "laundry" | "outside" | "bathroom" | "trash" | "tidy";

export interface Theme {
  key: ThemeKey;
  /** The banner's title, "Kitchen week". */
  name: string;
  /** The banner's line: what pays extra. Plain, short. */
  line: string;
  /** Whether a task's title is one of the theme's chores. */
  matches: (title: string) => boolean;
}

const words = (list: ReadonlyArray<string>, whole: ReadonlyArray<string> = []) => (title: string) => titleMentions(list, title) || titleMentionsWord(whole, title);

/**
 * The themes, in the order the weeks go round them. Title words as the badges match them
 * (shared/achievements.ts): lowercase, anywhere in the title, with the few short words that would catch
 * other jobs matched whole. Family friendly and goofy, nothing more than a name and a line.
 */
export const THEMES: readonly Theme[] = [
  { key: "kitchen", name: "Kitchen week", line: "Kitchen chores pay extra.", matches: words([...DISH_WORDS, "kitchen", "fridge", "oven", "stove", "counter", "microwave", "sink", "cupboard", "pantry", "kettle", "toaster", "lunch", "dinner", "cook"]) },
  { key: "omie", name: "Omie week", line: "Omie's chores pay extra.", matches: isOmieJob },
  { key: "laundry", name: "Laundry week", line: "Laundry chores pay extra.", matches: words([...LAUNDRY_WORDS, "fold", "sheets", "iron", "hamper", "wash clothes"]) },
  { key: "outside", name: "Outside week", line: "Outside chores pay extra.", matches: words(["lawn", "mow", "yard", "garden", "snow", "shovel", "leaves", "rake", "weed", "garage", "driveway", "deck", "patio", "porch", "gutter"], ["plant", "plants", "water", "car"]) },
  { key: "bathroom", name: "Bathroom week", line: "Bathroom chores pay extra.", matches: words(["bathroom", "toilet", "shower", "tub", "mirror", "loo"]) },
  { key: "trash", name: "Trash week", line: "Trash chores pay extra.", matches: words(["trash", "garbage", "recycl", "compost", "rubbish"], ["bin", "bins"]) },
  { key: "tidy", name: "Tidy week", line: "Tidying chores pay extra.", matches: words(["vacuum", "sweep", "mop", "dust", "tidy", "floor", "declutter", "clutter", "organiz", "organis"]) },
];

/** The theme of the week `key`, going round THEMES one a week. */
export function themeFor(key: string): Theme {
  return THEMES[weekNumber(key) % THEMES.length]!;
}

/** The theme by its key, or null. */
export function themeByKey(key: string | null | undefined): Theme | null {
  return THEMES.find((t) => t.key === key) ?? null;
}

/** Whether a task's title matches the week's theme. */
export function themeMatches(theme: Theme | null, title: string | null | undefined): boolean {
  return !!theme && typeof title === "string" && theme.matches(title);
}

/** A matching chore pays this share of its starting gold on top (shared/bounty.ts bountyFor `themed`). */
export const THEME_BONUS = 0.5;

/** The task screen's line for a matching chore, in the streak line's place: "Kitchen week bonus". */
export function themeTaskLine(theme: Theme): string {
  return `${theme.name} bonus`;
}

// ---------- the house goal ----------

/** The fewest chores a goal ever asks for. */
export const GOAL_MIN = 10;
/** How many past weeks the target reads. */
export const GOAL_HISTORY_WEEKS = 4;
/** The stretch over the recent average. */
export const GOAL_STRETCH = 1.1;
/** The wood each peon of the house gets when the goal is reached. */
export const GOAL_WOOD = 20;

/**
 * The week's target from the chores the house finished in each of the past weeks (any number, the
 * newest first or last): their average, a tenth more, rounded up, never under GOAL_MIN. Weeks of
 * nothing count (a quiet week lowers the next goal), but with no history at all it is GOAL_MIN.
 */
export function goalTarget(pastWeeks: ReadonlyArray<number>): number {
  if (pastWeeks.length === 0) return GOAL_MIN;
  const avg = pastWeeks.reduce((a, b) => a + b, 0) / pastWeeks.length;
  return Math.max(GOAL_MIN, Math.ceil(avg * GOAL_STRETCH));
}

/** The week's goal as the board carries it. */
export interface HouseGoal {
  target: number;
  /** Chores finished this week so far (luxury tasks are not chores of the house, docs/house-week.md). */
  done: number;
  /** When the house reached it (ISO), or null. */
  reached_at: string | null;
  /** The wood each peon got (GOAL_WOOD when it was paid). */
  wood: number;
}

/** The house's week as the board carries it (BoardSummary.week). */
export interface HouseWeekSummary {
  /** "2026-W44". */
  week: string;
  /** The week's last instant's end (the next Monday 00:00), ISO, for "Ends Sunday". */
  end: string;
  /** The week's theme. Always present: the clients draw it only under themeWeekUI. */
  theme: ThemeKey;
  /** The goal, once the household has one this week (the first board read with houseGoalUI on). */
  goal: HouseGoal | null;
}

/** The bar's cells: how many of `cells` are filled for `done` of `target`, full only when reached. */
export function goalCells(done: number, target: number, cells = GOAL_BAR_CELLS): number {
  if (target <= 0) return cells;
  if (done >= target) return cells;
  return Math.min(cells - 1, Math.floor((Math.max(0, done) / target) * cells));
}
/** The progress bar's cells across the sign. */
export const GOAL_BAR_CELLS = 20;

// ---------- the words (plain; docs/product.md "Push notifications": the title says it, a few words, no numbers) ----------

export const HOUSE_WEEK_WORDS = {
  /** The sign's goal heading. */
  goalHead: "House goal",
  /** Under the bar once reached. */
  reached: `Reached! ${GOAL_WOOD} wood each.`,
  /** The push to everyone. */
  pushTitle: "House goal reached!",
  pushBody: "Wood for everyone.",
  /** The celebration sheet. */
  sheetTitle: "Goal reached!",
  sheetButton: "Hooray!",
} as const;

/** "12 of 30 chores". */
export function goalLine(goal: Pick<HouseGoal, "done" | "target">): string {
  return `${Math.min(goal.done, goal.target)} of ${goal.target} chores`;
}

/** The celebration sheet's line: "The house did 30 chores this week. 20 wood each." */
export function goalSheetLine(goal: Pick<HouseGoal, "target" | "wood">): string {
  return `The house did ${goal.target} chores this week. ${goal.wood || GOAL_WOOD} wood each.`;
}

/** The sign's name for a screen reader. */
export function weekSignLabel(theme: Theme | null, goal: HouseGoal | null): string {
  const parts: string[] = [];
  if (theme) parts.push(`${theme.name}. ${theme.line}`);
  if (goal) parts.push(`${HOUSE_WEEK_WORDS.goalHead}: ${goalLine(goal)}.${goal.reached_at ? ` ${HOUSE_WEEK_WORDS.reached}` : ""}`);
  return parts.join(" ");
}

/** Where a device keeps the newest week whose goal celebration it has shown. */
export const GOAL_SEEN_KEY = "op.goalSeen";

/** Whether the board's week should bring up the celebration on this device: reached, and a week the device has not celebrated. */
export function goalCelebrates(summary: HouseWeekSummary | null | undefined, seenWeek: string | null): boolean {
  return !!summary?.goal?.reached_at && summary.week !== seenWeek;
}
